
/**
 * Manages creation and cleanup of PerformanceObservers
 */
export class PerformanceObserverManager {
  private observers: PerformanceObserver[] = [];
  
  /**
   * Initialize all performance observers with callback functions
   */
  initializeObservers(callbacks: {
    onCoreWebVitals: (entry: PerformanceEntry) => void;
    onResource: (entry: PerformanceResourceTiming) => void;
    onInteraction: (entry: PerformanceEventTiming) => void;
    onPaint: (entry: PerformanceEntry) => void;
  }) {
    this.observeCoreWebVitals(callbacks.onCoreWebVitals);
    this.observeResources(callbacks.onResource);
    this.observeInteractions(callbacks.onInteraction);
    this.observePaintTiming(callbacks.onPaint);
  }
  
  /**
   * Observe Core Web Vitals metrics
   */
  private observeCoreWebVitals(callback: (entry: PerformanceEntry) => void) {
    try {
      const coreWebVitalsObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          callback(entry);
        }
      });
      
      coreWebVitalsObserver.observe({ 
        entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift', 'longtask'] 
      });
      this.observers.push(coreWebVitalsObserver);
    } catch (error) {
      console.warn('Core Web Vitals tracking not fully supported:', error);
    }
  }
  
  /**
   * Observe resource loading metrics
   */
  private observeResources(callback: (entry: PerformanceResourceTiming) => void) {
    try {
      const resourceObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (entry.entryType === 'resource') {
            callback(entry as PerformanceResourceTiming);
          }
        }
      });
      
      resourceObserver.observe({ entryTypes: ['resource'] });
      this.observers.push(resourceObserver);
    } catch (error) {
      console.warn('Resource timing tracking not fully supported:', error);
    }
  }
  
  /**
   * Observe user interactions metrics
   */
  private observeInteractions(callback: (entry: PerformanceEventTiming) => void) {
    try {
      if ('interactionCount' in PerformanceEventTiming.prototype) {
        const interactionObserver = new PerformanceObserver((entryList) => {
          for (const entry of entryList.getEntries()) {
            callback(entry as PerformanceEventTiming);
          }
        });
        
        interactionObserver.observe({ 
          type: 'event', 
          buffered: true
        });
        this.observers.push(interactionObserver);
      }
    } catch (error) {
      console.warn('Interaction timing tracking not fully supported:', error);
    }
  }
  
  /**
   * Observe paint timing metrics
   */
  private observePaintTiming(callback: (entry: PerformanceEntry) => void) {
    try {
      const paintObserver = new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          callback(entry);
        }
      });
      
      paintObserver.observe({ entryTypes: ['paint'] });
      this.observers.push(paintObserver);
    } catch (error) {
      console.warn('Paint timing tracking not fully supported:', error);
    }
  }
  
  /**
   * Disconnect all observers to clean up resources
   */
  disconnectAll(): void {
    this.observers.forEach(observer => {
      try {
        observer.disconnect();
      } catch (err) {
        console.warn('Error disconnecting observer:', err);
      }
    });
    this.observers = [];
  }
}
